import { Customer, Transaction, ImportResult, ImportError } from '../types';
import { v4 as uuidv4 } from 'uuid';


export const extractCSVHeaders = async (file: File): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    const chunkSize = 1024; 
    let offset = 0;
    let lineBuffer = '';
    let headersFound = false;

    const readChunk = () => {
      if (offset >= file.size) {
        reject(new Error('No headers found in file - file appears to be empty or contains no text'));
        return;
      }

      const slice = file.slice(offset, offset + chunkSize);
      fileReader.readAsText(slice);
    };

    fileReader.onload = (e) => {
      const chunk = e.target?.result as string;
      lineBuffer += chunk;

      
      let lineEndIndex;
      while ((lineEndIndex = lineBuffer.search(/\r?\n/)) !== -1) {
        if (headersFound) break;

        const line = lineBuffer.substring(0, lineEndIndex);
        lineBuffer = lineBuffer.substring(lineEndIndex + (lineBuffer[lineEndIndex] === '\r' ? 2 : 1));

        
        const trimmedLine = line.trim();
        if (trimmedLine && /[a-zA-Z0-9]/.test(trimmedLine)) {
          
          try {
            const headers = parseHeaderLine(trimmedLine);
            headersFound = true;
            resolve(headers);
            return;
          } catch (error) {
            reject(new Error(`Error parsing header line: ${error.message}`));
            return;
          }
        }
      }

      
      if (!headersFound && offset + chunkSize < file.size) {
        offset += chunkSize;
        readChunk();
      } else if (!headersFound) {
        
        
        const trimmedBuffer = lineBuffer.trim();
        if (trimmedBuffer && /[a-zA-Z0-9]/.test(trimmedBuffer)) {
          try {
            const headers = parseHeaderLine(trimmedBuffer);
            resolve(headers);
            return;
          } catch (error) {
            reject(new Error(`Error parsing header line: ${error.message}`));
            return;
          }
        }
        reject(new Error('No headers found in file - file appears to be empty or contains no text'));
      }
    };

    fileReader.onerror = () => {
      reject(new Error('Error reading file'));
    };

    
    readChunk();
  });
};


const parseHeaderLine = (line: string): string[] => {
  
  const commaHeaders = parseCSVLine(line, ',');
  
  
  const semicolonHeaders = parseCSVLine(line, ';');
  
  
  if (semicolonHeaders.length > commaHeaders.length && semicolonHeaders.length >= 2) {
    return semicolonHeaders;
  } else if (commaHeaders.length >= 2) {
    return commaHeaders;
  } else if (semicolonHeaders.length >= 1) {
    return semicolonHeaders;
  } else if (commaHeaders.length >= 1) {
    return commaHeaders;
  }
  
  throw new Error('Unable to parse header line - no valid delimiter found');
};


const parseCSVLine = (line: string, delimiter: string): string[] => {
  const headers: string[] = [];
  const parts = line.split(delimiter);
  
  for (const part of parts) {
    
    const cleaned = part.trim().replace(/^["']|["']$/g, '');
    headers.push(cleaned);
  }
  
  
  return headers.filter(header => header.length > 0);
};


export const validateAndProcessCSV = async (
  file: File,
  fieldMapping: Record<string, string>,
  requiredFields?: string[]
): Promise<ImportResult> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length === 0) {
          reject(new Error('Empty file'));
          return;
        }

        
        let headerLine = '';
        let dataStartIndex = 0;
        
        for (let i = 0; i < lines.length; i++) {
          const trimmedLine = lines[i].trim();
          if (trimmedLine && /[a-zA-Z0-9]/.test(trimmedLine)) {
            headerLine = trimmedLine;
            dataStartIndex = i + 1;
            break;
          }
        }

        if (!headerLine) {
          reject(new Error('No headers found in file'));
          return;
        }

        
        const headers = parseHeaderLine(headerLine);
        const dataRows = lines.slice(dataStartIndex);

        if (dataRows.length > 10000) {
          reject(new Error('File too large. Maximum 10,000 records allowed.'));
          return;
        }

        const errors: ImportError[] = [];
        const customers = new Map<string, Customer>();
        const transactions: Transaction[] = [];

        
        const required = requiredFields || ['customer_id', 'plan_id', 'product_id', 'amount', 'currency', 'billing_date', 'payment_status', 'payment_method'];
        const missingFields = required.filter(field => !fieldMapping[field]);
        
        if (missingFields.length > 0) {
          reject(new Error(`Missing required field mappings: ${missingFields.join(', ')}`));
          return;
        }

        
        const sampleLine = dataRows[0] || headerLine;
        const delimiter = (sampleLine.split(';').length > sampleLine.split(',').length) ? ';' : ',';

        dataRows.forEach((row, index) => {
          const rowNumber = dataStartIndex + index + 1; 
          const columns = parseCSVLine(row, delimiter);
          
          if (columns.length !== headers.length) {
            errors.push({
              row: rowNumber,
              field: 'row',
              value: row,
              message: 'Column count mismatch'
            });
            return;
          }

          const rowData: Record<string, string> = {};
          headers.forEach((header, i) => {
            rowData[header] = columns[i] || '';
          });

          
          required.forEach(field => {
            if (!rowData[fieldMapping[field]]) {
              errors.push({
                row: rowNumber,
                field,
                value: '',
                message: `${field} is required`
              });
            }
          });

          
          const isRevenueRow = ['customer_id', 'plan_id', 'product_id', 'amount', 'currency', 'billing_date', 'payment_status', 'payment_method']
            .every(f => required.includes(f));
          if (isRevenueRow && errors.filter(e => e.row === rowNumber).length === 0) {
            const customerId = rowData[fieldMapping['customer_id']];
            const planId = rowData[fieldMapping['plan_id']];
            const productId = rowData[fieldMapping['product_id']];
            const amountStr = rowData[fieldMapping['amount']];
            const currency = rowData[fieldMapping['currency']];
            const billingDateStr = rowData[fieldMapping['billing_date']];
            const paymentStatus = rowData[fieldMapping['payment_status']];
            const paymentMethod = rowData[fieldMapping['payment_method']];
            const amount = parseFloat(amountStr);
            const billingDate = new Date(billingDateStr);
            
            
            if (!customers.has(customerId)) {
              customers.set(customerId, {
                id: customerId,
                name: `Customer ${customerId}`,
                email: `customer${customerId}@example.com`,
                status: 'active',
                createdAt: new Date(),
                totalRevenue: 0,
                currency: currency,
                mrr: 0,
                arr: 0,
                ltv: 0
              });
            }
            
            
            const transaction: Transaction = {
              id: uuidv4(),
              customerId,
              productId,
              amount,
              currency,
              billingDate,
              paymentStatus: paymentStatus as 'successful' | 'failed' | 'pending' | 'refunded',
              paymentMethod,
              description: `Payment for ${productId} (${planId})`
            };
            transactions.push(transaction);
            
            
            const customer = customers.get(customerId)!;
            if (paymentStatus === 'successful') {
              customer.totalRevenue += amount;
              customer.mrr = amount; 
              customer.arr = customer.mrr * 12;
              customer.ltv = customer.totalRevenue * 1.5; 
              customer.lastPaymentDate = billingDate;
            }
          }
        });

        const result: ImportResult = {
          totalRecords: dataRows.length,
          successfulRecords: dataRows.length - errors.length,
          failedRecords: errors.length,
          errors,
          customers: Array.from(customers.values()),
          transactions
        };

        resolve(result);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };

    reader.readAsText(file);
  });
};



export const extractCSVStatuses = async (file: File, statusHeader: string): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length === 0) reject(new Error('Empty file'));
        
        let headerLine = '';
        let dataStartIndex = 0;
        for (let i = 0; i < lines.length; i++) {
          const trimmedLine = lines[i].trim();
          if (trimmedLine && /[a-zA-Z0-9]/.test(trimmedLine)) {
            headerLine = trimmedLine;
            dataStartIndex = i + 1;
            break;
          }
        }
        if (!headerLine) reject(new Error('No headers found in file'));
        
        const headers = parseHeaderLine(headerLine);
        const statusIdx = headers.findIndex(
          h => h.trim().toLowerCase() === statusHeader.trim().toLowerCase()
        );
        if (statusIdx === -1) reject(new Error(`No column named "${statusHeader}" found`));
        const dataRows = lines.slice(dataStartIndex);
        const delimiter = (dataRows[0]?.split(';').length > dataRows[0]?.split(',').length) ? ';' : ',';
        const statuses = new Set<string>();
        dataRows.forEach(row => {
          const cols = parseCSVLine(row, delimiter);
          if (cols[statusIdx]) statuses.add(cols[statusIdx].trim());
        });
        resolve(Array.from(statuses).filter(s => s.length > 0));
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error('Error reading file'));
    reader.readAsText(file);
  });
};
